import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeftRight, RotateCcw, Shield } from 'lucide-react';
import { getInternalTenantDetail, overrideInternalTenantPlan } from '../services/internalControlPlaneService';

const PLAN_OPTIONS: Array<{ id: 'tester' | 'pro' | 'trial'; label: string; help: string }> = [
  { id: 'tester', label: 'Tester', help: 'Internal testing mode with active subscription status.' },
  { id: 'pro', label: 'Pro', help: 'Paid tier simulation for support and billing verification.' },
  { id: 'trial', label: 'Trial', help: 'Rollback-friendly tenant trial state (14 days).'},
];

export const InternalTenantBillingActionsView: React.FC = () => {
  const params = useParams<{ companyId: string }>();
  const companyId = String(params.companyId || '').trim();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [targetPlan, setTargetPlan] = useState<'tester' | 'pro' | 'trial'>('tester');
  const [tenantName, setTenantName] = useState<string>('');
  const [currentPlan, setCurrentPlan] = useState<string>('');
  const [currentStatus, setCurrentStatus] = useState<string>('');
  const [rollbackPlan, setRollbackPlan] = useState<'tester' | 'pro' | 'trial' | null>(null);

  const refresh = async () => {
    const detail = await getInternalTenantDetail(companyId);
    setTenantName(detail.company.name);
    setCurrentPlan(detail.company.plan);
    setCurrentStatus(detail.company.subscriptionStatus || 'none');
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!companyId) {
        setError('Company ID is required.');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const detail = await getInternalTenantDetail(companyId);
        if (cancelled) return;
        setTenantName(detail.company.name);
        setCurrentPlan(detail.company.plan);
        setCurrentStatus(detail.company.subscriptionStatus || 'none');
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Unable to load tenant billing context.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [companyId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-fade-in">
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-slate-800">Billing Actions</h2>
            <p className="text-sm text-slate-500 mt-1">
              Tenant: <span className="font-medium text-slate-700">{tenantName || companyId}</span>
            </p>
            <p className="text-xs text-slate-500">Company ID: {companyId}</p>
          </div>
          <Link
            to={`/internal/tenants/${encodeURIComponent(companyId)}`}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50"
          >
            <ArrowLeftRight size={14} />
            Back to tenant detail
          </Link>
        </div>
      </div>

      {error && (
        <div className="bg-white rounded-xl border border-red-200 p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-800">Action unavailable</h3>
              <p className="text-sm text-red-600 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
          <Shield className="w-5 h-5 text-slate-500" />
          Plan override (internal only)
        </h3>
        <p className="text-sm text-slate-500">
          This command is restricted to super admins. A reason is mandatory and every change is audited.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="border border-slate-200 rounded-lg px-3 py-2">
            <p className="text-xs uppercase tracking-wide text-slate-500">Current plan</p>
            <p className="text-sm font-medium text-slate-800 mt-1">{currentPlan || '—'}</p>
          </div>
          <div className="border border-slate-200 rounded-lg px-3 py-2">
            <p className="text-xs uppercase tracking-wide text-slate-500">Current subscription status</p>
            <p className="text-sm font-medium text-slate-800 mt-1">{currentStatus || '—'}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {PLAN_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => setTargetPlan(option.id)}
              className={`text-left border rounded-lg px-3 py-3 transition-colors ${
                targetPlan === option.id
                  ? 'border-indigo-400 bg-indigo-50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <p className="font-semibold text-slate-800">{option.label}</p>
              <p className="text-xs text-slate-500 mt-1">{option.help}</p>
            </button>
          ))}
        </div>

        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason for this plan override (required)"
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm min-h-[80px]"
        />

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            disabled={saving}
            onClick={async () => {
              const trimmed = reason.trim();
              if (!trimmed) {
                alert('Reason is required.');
                return;
              }
              if (!window.confirm(`Apply plan override to "${targetPlan}" for this tenant?`)) return;

              setSaving(true);
              setError(null);
              try {
                const result = await overrideInternalTenantPlan({
                  companyId,
                  targetPlan,
                  reason: trimmed,
                });
                const previous = String(result.previousPlan || '').toLowerCase();
                if (previous === 'tester' || previous === 'pro' || previous === 'trial') {
                  setRollbackPlan(previous);
                } else {
                  setRollbackPlan(null);
                }
                setReason('');
                await refresh();
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Unable to apply plan override.');
              } finally {
                setSaving(false);
              }
            }}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-400"
          >
            {saving ? 'Applying...' : `Apply ${targetPlan} override`}
          </button>

          {rollbackPlan && (
            <button
              type="button"
              disabled={saving}
              onClick={async () => {
                const trimmed = reason.trim();
                if (!trimmed) {
                  alert('Reason is required for rollback.');
                  return;
                }
                if (!window.confirm(`Rollback tenant plan to "${rollbackPlan}"?`)) return;

                setSaving(true);
                setError(null);
                try {
                  await overrideInternalTenantPlan({
                    companyId,
                    targetPlan: rollbackPlan,
                    reason: trimmed,
                  });
                  setReason('');
                  await refresh();
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'Unable to rollback tenant plan.');
                } finally {
                  setSaving(false);
                }
              }}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:bg-slate-100"
            >
              <RotateCcw size={14} />
              Quick rollback to {rollbackPlan}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
